import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Download, Eye, Loader2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

/**
 * DeckCard - Reusable card component for displaying deck previews
 * Used on Home page and Sample Decks page
 */
function DeckCard({
  deck,
  language = 'EN',
  onLoadDeck,
  showViewButton = true,
  showLoadButton = true,
  loading = false,
  className,
}) {
  const navigate = useNavigate();

  // Get localized name
  const getDeckName = () => {
    if (language === 'JP' && deck.name_ja) {
      return deck.name_ja;
    }
    return deck.name;
  };

  // Get localized description
  const getDeckDescription = () => {
    if (language === 'JP' && deck.description_ja) {
      return deck.description_ja;
    }
    return deck.description;
  };

  const handleView = () => {
    navigate(`/decks/view/${deck.id}`);
  };

  const handleLoad = () => {
    if (onLoadDeck) {
      onLoadDeck(deck);
    }
  };

  return (
    <Card
      className={cn(
        'group overflow-hidden transition-all duration-300',
        'hover:shadow-xl hover:-translate-y-1',
        'border border-border/50',
        className
      )}
    >
      {/* Deck Image */}
      <div className="relative aspect-[3/4] overflow-hidden bg-muted">
        {deck.imageUrl ? (
          <img
            src={deck.imageUrl}
            alt={getDeckName()}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
            <span className="text-6xl">🃏</span>
          </div>
        )}
        
        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
          {showViewButton && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleView}
              className="gap-1"
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          )}
          {showLoadButton && onLoadDeck && (
            <Button
              variant="default"
              size="sm"
              onClick={handleLoad}
              disabled={loading}
              className="gap-1"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Load
            </Button>
          )}
        </div>
      </div>

      {/* Deck Info */}
      <CardContent className="p-4">
        <h3 className="font-semibold text-base mb-1 truncate">
          {getDeckName()}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-2">
          {getDeckDescription()}
        </p>
      </CardContent>
    </Card>
  );
}

DeckCard.propTypes = {
  deck: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    name_ja: PropTypes.string,
    description: PropTypes.string,
    description_ja: PropTypes.string,
    imageUrl: PropTypes.string,
    deckCode: PropTypes.string,
  }).isRequired,
  language: PropTypes.string,
  onLoadDeck: PropTypes.func,
  showViewButton: PropTypes.bool,
  showLoadButton: PropTypes.bool,
  loading: PropTypes.bool,
  className: PropTypes.string,
};

export default DeckCard;
